import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

function getFocusableElements(root: HTMLElement | null) {
  if (!root) return []

  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((el) => {
    if (el.hasAttribute('disabled') || el.getAttribute('aria-hidden') === 'true') {
      return false
    }

    const style = window.getComputedStyle(el)
    return style.visibility !== 'hidden' && style.display !== 'none'
  })
}

type UseFocusTrapOptions = {
  onEscape?: () => void
  restoreFocus?: boolean
}

/** Prevent keyboard focus from reaching page content behind an overlay. */
export function useInertAppContent(active: boolean) {
  useEffect(() => {
    if (!active) return

    const elements = Array.from(document.querySelectorAll('main, footer'))
    elements.forEach((el) => el.setAttribute('inert', ''))

    return () => {
      elements.forEach((el) => el.removeAttribute('inert'))
    }
  }, [active])
}

export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
  { onEscape, restoreFocus = true }: UseFocusTrapOptions = {}
) {
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!active || !containerRef.current) return

    const container = containerRef.current
    previousFocusRef.current = document.activeElement as HTMLElement | null

    const focusable = getFocusableElements(container)
    if (focusable.length > 0) {
      focusable[0].focus()
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onEscape?.()
        return
      }

      if (event.key !== 'Tab') return

      const elements = getFocusableElements(container)
      if (elements.length === 0) return

      const first = elements[0]
      const last = elements[elements.length - 1]
      const activeElement = document.activeElement as HTMLElement | null

      if (event.shiftKey) {
        if (activeElement === first || !container.contains(activeElement)) {
          event.preventDefault()
          last.focus()
        }
        return
      }

      if (activeElement === last || !container.contains(activeElement)) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      if (restoreFocus) {
        previousFocusRef.current?.focus?.()
      }
    }
  }, [active, containerRef, onEscape, restoreFocus])
}
